import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import useApi from "../hooks/useApi";
import { useAuth } from "./AuthContext";

const LocationContext = createContext();

export const LocationProvider = ({ children }) => {
  const { publicAuth } = useApi();
  const { setCurrentCity } = useAuth();

  const [location, setLocation] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | denied | error
  const [error, setError] = useState(null);
  const inFlightRef = useRef(false);

  const clearError = () => setError(null);

  const reverseGeocode = async (lat, lng) => {
    const response = await publicAuth(
      "https://nominatim.openstreetmap.org/reverse",
      "GET",
      {
        lat,
        lon: lng,
        format: "json",
        addressdetails: 1,
        "accept-language": "en"
      },
      {
        headers: {
          "User-Agent": "ASYSTA Training Academy App"
        }
      }
    );

    const data = response?.data || {};
    const address = data.address || {};
    const city =
      address.city ||
      address.town ||
      address.village ||
      address.county ||
      address.state ||
      null;

    return {
      address: data.display_name || null,
      city,
      zipcode: address.postcode || null
    };
  };

  const requestLocation = async ({ force = false } = {}) => {
    if (inFlightRef.current) return;
    if (!force && status === "ready") return;

    if (!navigator.geolocation) {
      setStatus("error");
      setError("Geolocation is not supported by this browser.");
      return;
    }

    inFlightRef.current = true;
    setStatus("loading");
    setError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const latitude = Number(position.coords.latitude.toFixed(7));
          const longitude = Number(position.coords.longitude.toFixed(7));
          const accuracy = position.coords.accuracy;

          const geo = await reverseGeocode(latitude, longitude);
          const nextLocation = {
            latitude,
            longitude,
            accuracy,
            city: geo.city,
            zipcode: geo.zipcode,
            address: geo.address
          };

          setLocation(nextLocation);
          if (geo.city) setCurrentCity(geo.city);
          setStatus("ready");
        } catch (err) {
          setStatus("error");
          setError("Unable to fetch location details.");
        } finally {
          inFlightRef.current = false;
        }
      },
      (err) => {
        if (err?.code === err.PERMISSION_DENIED) {
          setStatus("denied");
          setError("Location access denied. Please enable location permissions.");
        } else if (err?.code === err.POSITION_UNAVAILABLE) {
          setStatus("error");
          setError("Location information unavailable.");
        } else if (err?.code === err.TIMEOUT) {
          setStatus("error");
          setError("Location request timed out.");
        } else {
          setStatus("error");
          setError("Unable to get your location.");
        }
        inFlightRef.current = false;
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000
      }
    );
  };

  useEffect(() => {
    let isMounted = true;

    const checkPermissionAndLocate = async () => {
      if (!navigator.permissions?.query) return;
      try {
        const result = await navigator.permissions.query({ name: "geolocation" });
        if (!isMounted) return;
        if (result.state === "granted") {
          requestLocation();
        }
      } catch (err) {
        // ignore permission query errors
      }
    };

    checkPermissionAndLocate();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <LocationContext.Provider
      value={{
        location,
        status,
        error,
        requestLocation,
        clearError
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useLocationContext = () => useContext(LocationContext);
